import { useEffect, useMemo } from "react";
import { useSelector, useStore } from "react-redux";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { userSelector } from "./store/user/slice";
import { loadingSelector } from "./store/loading/slice";
import { useAppDispatch } from "./store/index";
import type { RootState } from "./store/index";
import { installAuthInterceptors, setUnauthenticatedHandler } from "./auth/auth-client";
import { AuthStore, bootstrapSession, forgetLocalSession, handleUnauthenticated, refreshSession } from "./auth/auth-controller";
import { AuthSplash, AuthUnreachable } from "./components/auth-gate";

import NavMenu from './components/nav';
import Sale from './components/sale';
import Product from './components/product/product';
import BarcodePrint from './components/product/barcode-print';
import PriceTagPrint from './components/product/price-tag-print';
import CheckStock from "./components/check_stock/check-stock";
import GoodReceive from "./components/good_receive/good-receive";
import Separate from "./components/separate/separate";
import Include from "./components/include/include";
import Withdraw from "./components/withdraw/withdraw";
import ConfigGeneral from "./components/config/general/index";
import ConfigSeparate from "./components/config/separate/config-separate";
import Employee from "./components/employee/employee";
import Login from "./components/login";
import Vendor from "./components/vendor/vendor";
import Member from "./components/member/member";
import MemberGroup from "./components/member_group/member-group";
import ProductType from "./components/config/product_type/product-type";
import ProductUnit from "./components/config/product_unit/product-unit";
import PaymentType from "./components/config/payment_type/payment-type";
import PurchaseOrder from "./components/purchase_order/purchase-order";
import ReportDashboard from "./components/report";
import Expense from "./components/expense/expense";
import ConsignmentPage from "./components/consignment/consignment";

import './App.css';
import './assets/css/login.css';
import './assets/css/auth-gate.css';
import './assets/css/nav.css';
import './assets/css/sale.css';
import './assets/css/config.css';
import './assets/css/antd.overwrite.css';
import './assets/css/react.datatime.overwrite.css';
import './assets/css/arrow.down.css';
import './assets/css/expense.css';
import './assets/css/consignment.css';
import './assets/css/product-management.css';
// Keep the shared table-action contract last so feature styles cannot resize it.
import './assets/css/table.list.css';

/**
 * The theme is normally applied by nav.tsx, which is not mounted on the
 * splash or the login page. Applying the stored choice here too means a
 * dark-mode user does not get a white flash on every refresh.
 */
const applyStoredTheme = () => {
    try {
        const theme = window.localStorage.getItem('theme') || 'light';
        document.documentElement.setAttribute('data-theme', theme);
    } catch (e) {
        // Storage unavailable: leave the default theme.
    }
};

/**
 * Phase 7C-4 -- the auth gate.
 *
 * Before this phase, the effect here decoded `localStorage.uuid` (the
 * whole legacy login response) and treated its presence as being logged
 * in, so closing the browser never ended a session and nothing on the
 * server could end one either. Now the ONLY thing that can put protected
 * UI on screen is a `SESSION_ME` answer from the Backend.
 *
 *   checking     -> splash (no nav, no routes, no login flash)
 *   unreachable  -> retry screen; the token is kept
 *   signedOut    -> the login page, whatever the URL was
 *   signedIn     -> nav + routes; `/login` and `/` go to `default_page`
 */
function App() {

    const dispatch = useAppDispatch();
    const reduxStore = useStore<RootState>();
    const authStore: AuthStore = useMemo(
        () => ({ dispatch, getState: reduxStore.getState }),
        [dispatch, reduxStore]
    );
    const location = useLocation();
    const user = useSelector(userSelector);
    const loadingReducer = useSelector(loadingSelector);

    useEffect(() => {
        applyStoredTheme();
        installAuthInterceptors();
        setUnauthenticatedHandler((reason) => handleUnauthenticated(authStore, reason));
        bootstrapSession(authStore);
        return () => {
            setUnauthenticatedHandler(null);
        };
    }, [authStore]);

    const isLoginPage = location.pathname === "/login";
    const loading = loadingReducer.active
        ? <div className="lds-roller"><div></div><div></div><div></div><div></div><div></div><div></div><div></div><div></div></div>
        : null;

    if (user.authStatus === 'checking') {
        return (
            <div className='App'>
                { loading }
                <AuthSplash />
            </div>
        );
    }

    if (user.authStatus === 'unreachable') {
        return (
            <div className='App'>
                { loading }
                <AuthUnreachable
                    onRetry={() => refreshSession(authStore)}
                    onForget={() => forgetLocalSession(authStore)}
                />
            </div>
        );
    }

    if (user.authStatus !== 'signedIn') {
        return (
            <div className='App'>
                { loading }
                { isLoginPage
                    ? <Routes>
                        <Route path="/login" element={<Login />} />
                      </Routes>
                    : <Navigate to="/login" replace />
                }
            </div>
        );
    }

    if (isLoginPage || location.pathname === "/") {
        return <Navigate to={`/${user.default_page || "sale"}`} replace />;
    }

    return (
        <div className='App'>
            { loading }
            <div className='nav-container'>
                <NavMenu></NavMenu>
            </div>
            <div className='content-container'>
                <Routes>
                    <Route path="/sale" element={<Sale />} />
                    <Route path="/product" element={<Product />} />
                    <Route path="/product/barcode-print" element={<BarcodePrint />} />
                    <Route path="/product/price-tag-print" element={<PriceTagPrint />} />
                    <Route path="/price-tag/print" element={<PriceTagPrint />} />
                    <Route path="/purchase/order" element={<PurchaseOrder />} />
                    <Route path="/check/stock" element={<CheckStock />} />
                    <Route path="/good/receive" element={<GoodReceive />} />
                    <Route path="/consignment" element={<ConsignmentPage />} />
                    <Route path="/separate" element={<Separate />} />
                    <Route path="/include" element={<Include />} />
                    <Route path="/withdraw" element={<Withdraw />} />

                    <Route path="/member" element={<Member />} />
                    <Route path="/member/group" element={<MemberGroup />} />

                    <Route path="/config/general" element={<ConfigGeneral />} />
                    <Route path="/config/separate" element={<ConfigSeparate />} />

                    <Route path="/employee" element={<Employee />} />
                    <Route path="/vendor" element={<Vendor />} />
                    <Route path="/product/type" element={<ProductType />} />
                    <Route path="/product/unit" element={<ProductUnit />} />
                    <Route path="/payment/type" element={<PaymentType />} />
                    <Route path="/expense" element={<Expense />} />
                    <Route path="/report" element={<ReportDashboard />} />
                </Routes>
            </div>
        </div>
    );
}

export default App;
